import * as React from 'react';
import { AlertTriangle, Eye, Info, Sparkles } from 'lucide-react';
import { AreaAddSelect, AreaChip } from '@/components/ui/area-chip';
import {
  Callout,
  CodeBadge,
  DetailHead,
  SegmentedControl,
  Step,
  Steps,
  TriCheckbox,
  WorkspaceButton,
  WorkspaceField,
  WorkspaceInput,
  WorkspaceSelect,
  linkishClass,
  useToast,
} from '@/components/ui/workspace';
import { useCoverageQuery, useDiscoverySummaryQuery } from '@/lib/api/discovery';
import { useFrameworkContentQuery } from '@/lib/api/discovery-frameworks';
import {
  createInterview,
  useDiscoveryWrite,
  useInterviewPlanQuery,
  usePersonCheckQuery,
  type Candidate,
  type InterviewPlan,
} from '@/lib/api/discovery-sessions';
import {
  confirmInterviewProposal,
  type InterviewProposal,
} from '@/lib/api/discovery-interview-proposals';
import { PersonAvatar } from '../coverage-bits';
import { plural } from '../discovery-logic';
import { duplicateCandidates, errorText } from './errors';
import {
  addProblem,
  buildCreateBody,
  emptyAddDraft,
  formatWhen,
  isIncluded,
  toConfirmBody,
  toIsoInstant,
  unansweredCandidates,
  type AddDraft,
} from './sessions-logic';

/**
 * Add an interview (E12-F04 §1; rule 6; Decisions 54.13, 54.14). Who, with
 * the same-person question answered before anything can be saved; the type
 * suggested from the job title and department (code, not AI); the areas;
 * the questions from the type, each area and AI gap questions (each saying
 * why); the on-screen asks; how the questionnaire goes; and when.
 * Prototype: `addInterviewForm` (`#sessions/new`).
 */

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const LENGTHS = [30, 45, 60, 90];

/**
 * The add form pre-filled from a transcript placement (T-0261): its first
 * person (an existing record, or a new name with a title), its type, and
 * its areas when it named any.
 */
export function draftFromPlacement(p: InterviewProposal): AddDraft {
  const first = p.people[0];
  const draft = emptyAddDraft();
  if (first?.person_id) {
    draft.sameAs = {
      person_id: first.person_id,
      full_name: first.full_name,
      title: first.title,
      department: null,
      email: null,
      match: 'name',
      possible_duplicate: false,
      interviews: [],
    };
  } else if (first) {
    draft.name = first.full_name;
    draft.title = first.title ?? '';
  }
  if (p.interview_type) draft.typeId = p.interview_type.id;
  else if (p.areas.length > 0) draft.typeId = 'other';
  if (p.areas.length > 0) draft.areaIds = p.areas.map((a) => a.id);
  return draft;
}

export function AddInterview({
  engagementId,
  nextCode,
  onCancel,
  onAdded,
  placement,
}: {
  engagementId: string;
  /** The code the API will give it, once the list is loaded (rule 20). */
  nextCode: string | null;
  onCancel: () => void;
  onAdded: (interviewId: string) => void;
  /**
   * A transcript placement being changed (T-0261): the form starts from the
   * proposal and its submit confirms the placement with this interview,
   * which attaches the transcript and starts the reading.
   */
  placement?: InterviewProposal;
}) {
  const { toast } = useToast();
  const [a, setA] = React.useState<AddDraft>(() =>
    placement ? draftFromPlacement(placement) : emptyAddDraft(),
  );
  const [refused, setRefused] = React.useState<Candidate[]>([]);
  const patch = (p: Partial<AddDraft>) => setA((x) => ({ ...x, ...p }));

  const summary = useDiscoverySummaryQuery(engagementId);
  const coverage = useCoverageQuery(engagementId);
  const content = useFrameworkContentQuery(summary.data?.framework?.id ?? null, 'current');
  const types = (content.data?.interview_types ?? [])
    .filter((t) => t.status === 'active')
    .sort((x, y) => x.sort_order - y.sort_order);

  // The same-person check runs on the typed name (and email) as it settles.
  const name = useDebounced(a.name.trim().replace(/\s+/g, ' '));
  const email = useDebounced(a.email.trim());
  const check = usePersonCheckQuery(
    engagementId,
    name,
    email,
    !a.sameAs && name.split(' ').length >= 2,
  );
  const candidates = React.useMemo(() => {
    const all = [...(check.data?.candidates ?? []), ...refused];
    return all.filter((c, i) => all.findIndex((x) => x.person_id === c.person_id) === i);
  }, [check.data, refused]);
  const ask = unansweredCandidates(a, candidates)[0];

  const title = useDebounced(a.sameAs ? (a.sameAs.title ?? '') : a.title.trim());
  const department = useDebounced(a.sameAs ? (a.sameAs.department ?? '') : a.department.trim());
  const planQ = useInterviewPlanQuery(
    engagementId,
    {
      title,
      department,
      ...(a.typeId ? { interview_type_id: a.typeId } : {}),
      ...(a.areaIds ? { area_ids: a.areaIds } : {}),
    },
    true,
  );
  const plan = planQ.data;
  const why = addProblem(a, plan, candidates);

  const create = useDiscoveryWrite(engagementId, async (p: InterviewPlan) => {
    if (placement) {
      const r = await confirmInterviewProposal(
        engagementId,
        placement.proposal_id,
        toConfirmBody(buildCreateBody(a, p)),
      );
      return {
        id: r.interview.id,
        code: r.interview.code,
        scheduled_at: null,
        questionnaire_links_sent: 0,
        placed: true as const,
      };
    }
    return {
      ...(await createInterview(engagementId, buildCreateBody(a, p))),
      placed: false as const,
    };
  });
  const onSave = () => {
    if (!plan || why) return;
    const who = a.sameAs?.full_name ?? a.name.trim();
    const n = plan.questions.filter((q) => isIncluded(q.key, q.default_included, a.ticks)).length;
    create.mutate(plan, {
      onSuccess: (r) => {
        if (r.placed) {
          toast({
            message: `${placement!.document.title} is now ${r.code} with ${who || 'its speakers'}: the reading starts now.`,
          });
          onAdded(r.id);
          return;
        }
        const qn = plan.questionnaire;
        let linkMsg = '';
        if (qn && a.channel === 'link') {
          linkMsg =
            r.questionnaire_links_sent > 0
              ? ` The ${qn.name} questionnaire link went to ${who}.`
              : r.scheduled_at
                ? ` The ${qn.name} questionnaire goes by secure link with the invite.`
                : ` The ${qn.name} questionnaire was not sent: add an email for ${who} first.`;
        }
        toast({
          message: `Added ${r.code} with ${who}: ${plural(n, 'question')}${
            r.scheduled_at ? `, ${formatWhen(r.scheduled_at)}` : ''
          }.${linkMsg}`,
        });
        onAdded(r.id);
      },
      onError: (e) => {
        const dup = duplicateCandidates(e);
        if (dup) {
          setRefused(dup);
          patch({ sameAs: null, different: [] });
          return;
        }
        toast({ message: errorText(e), tone: 'critical' });
      },
    });
  };

  const areaLabel = (id: string) => coverage.data?.areas.find((x) => x.area_id === id)?.label ?? id;
  const corroborated = (id: string | null) =>
    !!id && coverage.data?.areas.find((x) => x.area_id === id)?.status === 'corroborated';
  const typeValue = a.typeId ?? plan?.type?.id ?? '';
  const nIncluded = plan
    ? plan.questions.filter((q) => isIncluded(q.key, q.default_included, a.ticks)).length
    : 0;
  const hasPlan = !!plan && (!!plan.type || plan.area_ids.length > 0);

  return (
    <div>
      <DetailHead
        leading={nextCode ? <CodeBadge size="lg">{nextCode}</CodeBadge> : undefined}
        title={placement ? `Place ${placement.document.title}` : 'Add an interview'}
        meta={
          placement
            ? `${nextCode ? `It gets the next code, ${nextCode}. ` : ''}Confirming creates the interview, attaches the transcript and starts the reading.`
            : `${nextCode ? `It gets the next code, ${nextCode}. ` : ''}Type the person’s title and Caliber suggests the interview type.`
        }
      />
      <Steps>
        <Step n={1} title="Who" state="current">
          {a.sameAs ? (
            <Callout
              tone="ok"
              actions={
                <button
                  type="button"
                  className={linkishClass()}
                  onClick={() =>
                    patch({ sameAs: null, different: [...a.different, a.sameAs!.person_id] })
                  }
                >
                  Someone else
                </button>
              }
            >
              <div className="flex items-center gap-2.5">
                <PersonAvatar id={a.sameAs.person_id} name={a.sameAs.full_name} size={28} />
                <div>
                  <div className="font-medium text-ink-900">{a.sameAs.full_name}</div>
                  <div className="text-meta text-ink-500">
                    {[a.sameAs.title, a.sameAs.department].filter(Boolean).join(', ')} · already in
                    this engagement
                  </div>
                </div>
              </div>
            </Callout>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <WorkspaceField label="Name">
                  {(id) => (
                    <WorkspaceInput
                      id={id}
                      autoComplete="off"
                      placeholder="First and last name"
                      value={a.name}
                      onChange={(e) => patch({ name: e.target.value, different: [] })}
                    />
                  )}
                </WorkspaceField>
                <WorkspaceField label="Title">
                  {(id) => (
                    <WorkspaceInput
                      id={id}
                      autoComplete="off"
                      placeholder="e.g. Accounts Payable Clerk"
                      value={a.title}
                      onChange={(e) => patch({ title: e.target.value })}
                    />
                  )}
                </WorkspaceField>
                <WorkspaceField label="Department">
                  {(id) => (
                    <WorkspaceInput
                      id={id}
                      autoComplete="off"
                      value={a.department}
                      onChange={(e) => patch({ department: e.target.value })}
                    />
                  )}
                </WorkspaceField>
                <WorkspaceField
                  label={
                    <>
                      Email{' '}
                      <span className="font-normal text-ink-500">
                        for the invite and secure links
                      </span>
                    </>
                  }
                >
                  {(id) => (
                    <WorkspaceInput
                      id={id}
                      type="email"
                      autoComplete="off"
                      value={a.email}
                      onChange={(e) => patch({ email: e.target.value })}
                    />
                  )}
                </WorkspaceField>
              </div>
              {ask ? (
                <Callout
                  tone="ask"
                  icon={AlertTriangle}
                  className="mt-3"
                  role="group"
                  actions={
                    <>
                      <WorkspaceButton variant="primary" onClick={() => patch({ sameAs: ask })}>
                        Yes, same person
                      </WorkspaceButton>
                      <WorkspaceButton
                        variant="tertiary"
                        onClick={() => patch({ different: [...a.different, ask.person_id] })}
                      >
                        No
                      </WorkspaceButton>
                    </>
                  }
                >
                  <div className="font-medium text-ink-900">
                    Is this {ask.full_name}
                    {ask.title ? `, ${ask.title}` : ''}?
                  </div>
                  <div className="text-meta text-ink-500">
                    Already in this engagement
                    {ask.interviews[0]
                      ? ` (${ask.interviews[0].code} ${ask.interviews[0].title})`
                      : ''}
                    . Using the same person keeps everything they say as one source.
                  </div>
                </Callout>
              ) : null}
            </>
          )}
        </Step>

        <Step n={2} title="Interview type and areas" state="current">
          <div className="flex flex-wrap items-center gap-2.5">
            <WorkspaceSelect
              aria-label="Interview type"
              className="max-w-[380px]"
              value={typeValue}
              onChange={(e) =>
                patch({ typeId: e.target.value, areaIds: undefined, ticks: {}, duration: null })
              }
            >
              <option value="" disabled>
                Pick an interview type
              </option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
              <option value="other">Other: pick the areas yourself</option>
            </WorkspaceSelect>
            {!a.typeId && plan?.type && plan.suggested_type_id === plan.type.id ? (
              <span className="flex items-center gap-1 text-meta text-ink-500">
                <Info className="h-3 w-3" aria-hidden="true" /> Suggested from the job title
              </span>
            ) : null}
          </div>
          <div className="mb-1.5 mt-3.5 text-meta font-medium text-ink-700">Areas you’ll cover</div>
          <div className="flex flex-wrap items-center gap-1">
            {(plan?.area_ids ?? []).map((id) => (
              <AreaChip
                key={id}
                onRemove={() => patch({ areaIds: (plan?.area_ids ?? []).filter((x) => x !== id) })}
              >
                {areaLabel(id)}
              </AreaChip>
            ))}
            <AreaAddSelect
              options={(coverage.data?.areas ?? [])
                .filter((x) => !(plan?.area_ids ?? []).includes(x.area_id))
                .map((x) => ({ id: x.area_id, label: x.label }))}
              onAdd={(id) => patch({ areaIds: [...(plan?.area_ids ?? []), id] })}
            />
          </div>
          <div className="mt-1.5 text-meta text-ink-500">
            Plans the interview and its questions. Coverage still comes only from confirmed
            evidence.
            {plan && plan.dropped_area_tags.length > 0
              ? ` Left out, not in this engagement’s framework version: ${plan.dropped_area_tags
                  .map((t) => t.label)
                  .join(', ')}.`
              : ''}
          </div>
        </Step>

        <Step
          n={3}
          title="Questions"
          state="current"
          summary={hasPlan ? `${nIncluded} selected. Untick what doesn’t apply.` : undefined}
        >
          {!hasPlan || !plan ? (
            <div className="text-meta text-ink-500">
              Pick an interview type or an area, and the questions appear here.
            </div>
          ) : (
            <PlanQuestions
              plan={plan}
              draft={a}
              onTick={(key, on) => patch({ ticks: { ...a.ticks, [key]: on } })}
              onChannel={(channel) => patch({ channel })}
              areaLabel={areaLabel}
              corroborated={corroborated}
            />
          )}
        </Step>

        {placement ? null : (
          <Step n={4} title="When" state="current" summary="Optional. You can schedule it later.">
            <div className="flex flex-wrap gap-2.5">
              <WorkspaceField label="When" className="w-[230px]">
                {(id) => (
                  <WorkspaceInput
                    id={id}
                    type="datetime-local"
                    value={a.when}
                    onChange={(e) => patch({ when: e.target.value })}
                  />
                )}
              </WorkspaceField>
              <WorkspaceField label="Length" className="w-[130px]">
                {(id) => (
                  <WorkspaceSelect
                    id={id}
                    value={String(a.duration ?? plan?.duration_minutes ?? 45)}
                    onChange={(e) => patch({ duration: Number(e.target.value) })}
                  >
                    {[...new Set([...LENGTHS, plan?.duration_minutes ?? 45])]
                      .sort((x, y) => x - y)
                      .map((m) => (
                        <option key={m} value={m}>
                          {m} min
                        </option>
                      ))}
                  </WorkspaceSelect>
                )}
              </WorkspaceField>
            </div>
            {toIsoInstant(a.when) ? (
              <div className="mt-1.5 text-meta text-ink-500">
                Adding it schedules it and sends the invite.
              </div>
            ) : null}
          </Step>
        )}
      </Steps>
      <div className="ml-10 mt-[18px] flex justify-end gap-2">
        {why && (a.name || a.sameAs) ? (
          <span className="flex-1 self-center text-meta text-ink-500">{why}.</span>
        ) : null}
        <WorkspaceButton variant="tertiary" size="md" onClick={onCancel}>
          Cancel
        </WorkspaceButton>
        <WorkspaceButton
          variant="primary"
          size="md"
          disabled={!!why || create.isPending}
          title={why ?? undefined}
          onClick={onSave}
        >
          {placement ? 'Confirm and attach' : `Add ${nextCode ?? 'interview'}`}
        </WorkspaceButton>
      </div>
    </div>
  );
}

function PlanQuestions({
  plan,
  draft,
  onTick,
  onChannel,
  areaLabel,
  corroborated,
}: {
  plan: InterviewPlan;
  draft: AddDraft;
  onTick: (key: string, on: boolean) => void;
  onChannel: (c: 'live' | 'link') => void;
  areaLabel: (id: string) => string;
  corroborated: (id: string | null) => boolean;
}) {
  const typeQs = plan.questions.filter((q) => q.kind === 'type');
  const aiQs = plan.questions.filter((q) => q.kind === 'ai');
  const byArea = plan.area_ids
    .map((id) => ({ id, qs: plan.questions.filter((q) => q.kind === 'area' && q.area_id === id) }))
    .filter((g) => g.qs.length > 0);
  const row = (key: string, on: boolean, text: React.ReactNode, extra?: React.ReactNode) => (
    <label
      key={key}
      className="flex cursor-pointer items-start gap-2.5 border-b border-ink-100 py-[7px] text-body-sm text-ink-900 last:border-b-0"
    >
      <span className="mt-0.5">
        <TriCheckbox
          state={on ? 'on' : 'off'}
          aria-label={typeof text === 'string' ? text : 'Include'}
          onChange={(next) => onTick(key, next)}
        />
      </span>
      <span className="min-w-0 flex-1">
        {text}
        {extra}
      </span>
    </label>
  );
  const group = (label: React.ReactNode, note?: string, ai = false) => (
    <div
      className={`mb-0.5 mt-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase leading-[18px] tracking-[0.06em] first:mt-0 ${ai ? 'text-ai-ink' : 'text-ink-500'}`}
    >
      {label}
      {note ? (
        <span className="ml-1.5 text-[11px] font-normal normal-case tracking-normal text-ink-500">
          {note}
        </span>
      ) : null}
    </div>
  );
  const on = (key: string, d: boolean) => isIncluded(key, d, draft.ticks);
  return (
    <>
      {typeQs.length > 0 ? (
        <>
          {group(plan.type?.name ?? 'Interview type')}
          {typeQs.map((q) => row(q.key, on(q.key, q.default_included), q.text))}
        </>
      ) : null}
      {byArea.map((g) => (
        <React.Fragment key={g.id}>
          {group(
            areaLabel(g.id),
            corroborated(g.id) ? 'Already corroborated, so unticked' : undefined,
          )}
          {g.qs.map((q) => row(q.key, on(q.key, q.default_included), q.text))}
        </React.Fragment>
      ))}
      {aiQs.length > 0 ? (
        <>
          {group(
            <>
              <Sparkles className="h-3 w-3" aria-hidden="true" /> Suggested by AI for what’s still
              missing
            </>,
            undefined,
            true,
          )}
          {aiQs.map((q) =>
            row(
              q.key,
              on(q.key, q.default_included),
              q.text,
              q.ai_reason ? (
                <span className="mt-0.5 block text-meta text-ai-ink">{q.ai_reason}</span>
              ) : null,
            ),
          )}
        </>
      ) : null}
      {plan.demos.length > 0 ? (
        <>
          {group('Ask to see on screen')}
          {plan.demos.map((x) =>
            row(
              x.key,
              on(x.key, x.default_included),
              <span className="inline-flex items-start gap-1.5">
                <Eye className="mt-[3px] h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />
                {x.text}
              </span>,
            ),
          )}
        </>
      ) : null}
      {plan.questionnaire ? (
        <>
          <div className="mt-3.5 flex flex-wrap items-center gap-2.5">
            <span className="text-meta text-ink-500">Questionnaire</span>
            <span className="text-body-sm font-medium text-ink-900">
              {plan.questionnaire.code} · {plan.questionnaire.name}
            </span>
            <SegmentedControl
              aria-label="How the questionnaire is answered"
              value={draft.channel}
              onChange={onChannel}
              options={[
                { value: 'live', label: 'Answer it in the session' },
                { value: 'link', label: 'Send it by secure link first' },
              ]}
            />
          </div>
          {draft.channel === 'link' ? (
            <div className="mt-1.5 text-meta text-ink-500">
              {toIsoInstant(draft.when)
                ? 'The link goes with the invite.'
                : 'No date yet, so the link goes as soon as you add the interview.'}
            </div>
          ) : null}
        </>
      ) : null}
      <div className="mt-2.5 text-meta text-ink-500">
        The type’s questions and each area’s questions are maintained in Firm settings → Discovery
        frameworks.
      </div>
    </>
  );
}
